import { useMemo } from 'react';
import { Platform } from 'react-native';

import type { PageSection } from '@24i/nxg-sdk-photon';
import { ROW_TYPES } from '@24i/nxg-sdk-photon';
import { Ratio } from '@24i/nxg-sdk-higgs';
import { DeviceInfo } from '@24i/nxg-sdk-quantum';
import { BASE_MENU_HEIGHT, BASE_GAP } from '@24i/nxg-core-utils/src/constants';
import { calculateTelegramGap } from '@24i/nxg-sdk-higgs/src/utils';

import { useFeature } from '../../context/AppSettingsData';
import { useShouldShowTopNavbar } from '../../hooks/useShouldShowTopNavbar';
import type { SectionSpacing, TitleConfig } from './types';

// Default title config for all sections
export const DEFAULT_TITLE_CONFIG: TitleConfig = {
    show: true,
    align: 'left',
};

// Per-section-type title config overrides (extend as needed)
export const TITLE_CONFIGS_BY_TYPE: Record<string, Partial<TitleConfig>> = {
    [ROW_TYPES.HERO_SLIDER]: { show: false },
    [ROW_TYPES.SLIDER]: { show: false },
    // Add more overrides as needed
};

// Web responsive horizontal padding configuration
// Centralized breakpoints and values for consistent spacing across web sections

// Platform default vertical spacing constant
export const getDefaultVerticalSpacing = () => {
    if (Platform.OS === 'web') return 48;
    if (Platform.isTV) return Ratio(64);
    if (DeviceInfo.isTablet()) return 40;
    return 32;
};

// Platform default horizontal spacing
export const getDefaultHorizontalSpacing = () => {
    // Web uses responsive padding via WEB_RESPONSIVE_PADDING config
    // (applied in styles.ts based on useDefaultPadding flag)
    if (Platform.OS === 'web') return 0;
    if (Platform.isTV) return Ratio(144);
    if (DeviceInfo.isTablet()) return 32;
    return 20;
};

// Lazy getter to avoid calling spacing functions at module load time
const getDefaultSectionSpacing = () => ({
    default: {
        top: 0,
        right: 0,
        bottom: getDefaultVerticalSpacing(),
        left: getDefaultHorizontalSpacing(),
        // webPaddingMode defaults to 'horizontal' (symmetric) on web
    },
    [ROW_TYPES.SINGLE_ROW]: {
        top: 0,
        bottom: getDefaultVerticalSpacing(),
        // Web: use responsive padding with 'left' mode (right=0)
        // Native: use platform defaults from getDefaultHorizontalSpacing()
        webPaddingMode: 'left' as const,
    },
    [ROW_TYPES.SINGLE_HIGHLIGHT]: {
        top: 0,
        bottom: getDefaultVerticalSpacing(),
        // Web: use responsive padding with 'left' mode (right=0)
        // Native: use platform defaults from getDefaultHorizontalSpacing()
        webPaddingMode: 'left' as const,
    },
    [ROW_TYPES.SLIDER]: {
        top: 0,
        bottom: 32,
        left: 0,
        right: 0,
    },
    [ROW_TYPES.HERO_SLIDER]: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
});

// Platform default spacing logic (for legacy compatibility)
export const getDefaultSectionVerticalSpacing = () => getDefaultVerticalSpacing();

// Helper: Check if object has a property key
export const hasProperty = (
    obj: Partial<SectionSpacing> | undefined,
    key: keyof SectionSpacing
): boolean => (obj ? key in obj : false);

// Helper: Merge spacing defaults with type and section overrides
export const mergeSpacing = (
    platformDefault: SectionSpacing,
    typeDefault: Partial<SectionSpacing>,
    sectionOverride: Partial<SectionSpacing>,
    section?: PageSection
): SectionSpacing => {
    const base = { ...platformDefault, ...typeDefault, ...sectionOverride };

    // Fallback for left/right if not explicitly set
    const overrideHasLeft = hasProperty(sectionOverride, 'left');
    const overrideHasRight = hasProperty(sectionOverride, 'right');
    const typeHasLeft = hasProperty(typeDefault, 'left');
    const typeHasRight = hasProperty(typeDefault, 'right');

    const left =
        !overrideHasLeft && !typeHasLeft && section?.type !== ROW_TYPES.HERO_SLIDER
            ? getDefaultSectionSpacing().default.left
            : base.left;

    const { right: baseRight } = base;
    let right = baseRight;
    if (!overrideHasRight && !typeHasRight && section?.type !== ROW_TYPES.HERO_SLIDER) {
        const defaultRight = getDefaultSectionSpacing().default.right;
        right = defaultRight !== undefined ? defaultRight : getDefaultSectionSpacing().default.left;
    }

    return { ...base, left, right };
};

// Helper: Apply backend override for vertical spacing
export const applyBackendOverride = (
    spacing: SectionSpacing,
    rowVerticalSpacing: { enabled?: boolean; verticalSpace?: number } | undefined
): SectionSpacing => {
    if (
        rowVerticalSpacing &&
        rowVerticalSpacing.enabled &&
        typeof rowVerticalSpacing.verticalSpace === 'number'
    ) {
        return { ...spacing, bottom: rowVerticalSpacing.verticalSpace };
    }
    return spacing;
};

// Helper: Ensure all spacing keys have values
export const ensureSpacingDefaults = (spacing: Partial<SectionSpacing>): SectionSpacing => ({
    top: spacing.top ?? 0,
    right:
        spacing.right ??
        getDefaultSectionSpacing().default.right ??
        getDefaultSectionSpacing().default.left,
    bottom: spacing.bottom ?? getDefaultSectionVerticalSpacing(),
    left: spacing.left ?? getDefaultSectionSpacing().default.left,
    between: spacing.between ?? 0,
    webPaddingMode: spacing.webPaddingMode, // Preserve web padding mode
});

// Helper: Merge title config
export const mergeTitleConfig = (section?: PageSection): TitleConfig => {
    const typeTitleConfig: Partial<TitleConfig> =
        section?.type && TITLE_CONFIGS_BY_TYPE[section.type]
            ? TITLE_CONFIGS_BY_TYPE[section.type]
            : {};
    const sectionTitleConfig: Partial<TitleConfig> =
        section && 'titleConfig' in section && section.titleConfig ? section.titleConfig : {};

    return {
        ...DEFAULT_TITLE_CONFIG,
        ...typeTitleConfig,
        ...sectionTitleConfig,
    };
};

// Hook to get section render options (spacing + titleConfig) with robust merging logic
export function useSectionRenderOptions(section?: PageSection): {
    spacing: SectionSpacing;
    titleConfig: TitleConfig;
    useDefaultPadding: boolean;
} {
    // Backend override for vertical spacing (bottom) only
    const rowVerticalSpacing = useFeature('rowVerticalSpacing');
    const { shouldShowTopNavbar } = useShouldShowTopNavbar();

    return useMemo(() => {
        // 1. Start with platform default
        const platformDefault: SectionSpacing = {
            top: 0,
            bottom: getDefaultSectionVerticalSpacing(),
            left: 0,
            right: 0,
            between: 0,
        };

        // 2. Get type and section overrides
        const typeDefault: Partial<SectionSpacing> =
            section?.type && getDefaultSectionSpacing()[section.type]
                ? getDefaultSectionSpacing()[section.type]
                : {};
        const sectionOverride: Partial<SectionSpacing> =
            section && 'spacing' in section && section.spacing ? section.spacing : {};

        // 3. Merge spacing with fallbacks
        const merged = mergeSpacing(platformDefault, typeDefault, sectionOverride, section);

        // 4. Calculate dynamic top spacing for SLIDER when no title is shown
        const titleConfig = mergeTitleConfig(section);
        if (section?.type === ROW_TYPES.SLIDER && !titleConfig.show && Platform.OS === 'web') {
            const menuHeight = shouldShowTopNavbar
                ? BASE_MENU_HEIGHT + calculateTelegramGap(2)
                : Math.max(calculateTelegramGap(), BASE_GAP);
            merged.top = menuHeight;
        }

        // 5. Apply backend override
        const spacingWithOverride = applyBackendOverride(merged, rowVerticalSpacing);

        // 6. Ensure all keys are present
        const spacing = ensureSpacingDefaults(spacingWithOverride);

        // 7. Determine if explicit padding was set
        const hasExplicitPadding =
            hasProperty(sectionOverride, 'left') ||
            hasProperty(sectionOverride, 'right') ||
            hasProperty(typeDefault, 'left') ||
            hasProperty(typeDefault, 'right');

        return { spacing, titleConfig, useDefaultPadding: !hasExplicitPadding };
    }, [section, rowVerticalSpacing, shouldShowTopNavbar]);
}
